import { createSlice, isAnyOf, type PayloadAction } from '@reduxjs/toolkit';
import { isDefined, isNotDefined } from '@sgme/fp';
import { formatISO9075 } from 'date-fns';

import { mifidApi } from '@/api/mifid';
import { getWorkspaceMatchFulfilled } from '@/api/workspace/index.ts';
import { mapToFxSwap } from '@/domain/FxSwap/FxSwap.utils.ts';
import { cashRepriceQuoteSwapReducer } from '@/store/blotter/workflows/cashRepriceQuote/extraReducers';
import { reduceSwapOnFetchSharedWorkspace } from '@/store/sharedWorkspace/workflows/getSharedTab.workflow.ts';
import { reduceSwapStateOnSharedTabRemoved } from '@/store/sharedWorkspace/workflows/onSharedTabRemoved.workflow.ts';
import { swapApi } from '@/store/swap/api/index.ts';
import { generateSwapTile } from '@/store/swap/helpers/tile.ts';
import { updateSwapFromTradeCapture } from '@/store/swap/helpers/trade-capture.ts';
import type { FxSwapRegular, SwapById, SwapQuote, SwapRfsPrice } from '@/store/swap/state.ts';
import { addTile, duplicateTile, removeTile, updateTileProductType } from '@/store/tiles/slice.ts';
import type { ReceivedBidAskValues, TradeCaptureChecked } from '@/store/types.ts';
import type { UserPreferencesState } from '@/store/userPreferences/state.ts';
import type { PredealCheckStatusIcon, PredealCheckStatusLabel } from '@/types/common.ts';
import { isIsoDate, type Timestamp } from '@/utils/date.ts';
import type { BidAskPoint } from '@/utils/number-point.ts';
import type { ReceivedSwapQuoteReply } from '@/utils/rfs/type.ts';

export const swapSlice = createSlice({
  name: 'swap',
  reducerPath: 'swap',

  initialState: {} as SwapById,

  reducers: {
    swapReseted: (state, action: PayloadAction<{ tileId: string; userPreferences: UserPreferencesState }>) => {
      const tile = state[action.payload.tileId];

      if (isNotDefined(tile)) {
        return;
      }

      state[action.payload.tileId] = generateSwapTile(tile.properties.currencyPair, action.payload.userPreferences);
    },

    swapClientIdUpdated: (
      state,
      action: PayloadAction<
        | { tileId: string; clientId: number | undefined; isTabClientOverriden: true }
        | { tileId: string; clientId: number; isTabClientOverriden: false }
      >,
    ) => {
      const tile = state[action.payload.tileId];

      if (isNotDefined(tile) || tile.clientId === action.payload.clientId) {
        return;
      }

      if (action.payload.isTabClientOverriden === true) {
        tile.clientId = action.payload.clientId;
      }
    },

    swapCommercialMarginSpotPointsUpdated: (
      state: SwapById,
      action: PayloadAction<{ tileId: string; value: BidAskPoint | undefined }>,
    ) => {
      const tile = state[action.payload.tileId];

      if (isNotDefined(tile)) {
        return;
      }
      // tile exists
      tile.properties.commercialMarginSpotPoints = action.payload.value;
    },

    swapCommercialMarginSwapPointsUpdated: (
      state: SwapById,
      action: PayloadAction<{ tileId: string; value: BidAskPoint | undefined }>,
    ) => {
      const tile = state[action.payload.tileId];

      if (isNotDefined(tile)) {
        return;
      }
      // tile exists

      tile.properties.commercialMarginSwapPoints = action.payload.value;
    },

    swapCommercialMarginCurrencyToggled: (state: SwapById, action: PayloadAction<{ tileId: string }>) => {
      const tile = state[action.payload.tileId];

      if (isNotDefined(tile)) {
        return;
      }
      // tile exists

      tile.properties.commercialMarginAmountCurrencyIndex =
        tile.properties.commercialMarginAmountCurrencyIndex === 1 ? 2 : 1;
    },

    // RFS

    swapRfsQuoteReceived: (
      state: SwapById,
      action: PayloadAction<{
        tileId: string;
        requestId: string;
        isWithdraw: boolean;
        reply: ReceivedSwapQuoteReply;
        backendTimestamp: Timestamp;
        frontendTimestamp: Timestamp;
      }>,
    ) => {
      const tile = state[action.payload.tileId];

      // do nothing if the tile non longer exists yet or if the RFS stream is closed in parallel
      if (isNotDefined(tile) || isNotDefined(tile.properties.rfs)) {
        return;
      }

      const { requestId, isWithdraw, reply } = action.payload;

      const convertBidAskValue = (value: ReceivedBidAskValues | null) =>
        isDefined(value) ? { bid: Number(value.Bid), ask: Number(value.Ask) } : { bid: 0, ask: 0 };

      const previousStreamedRfs = tile.properties.rfs?.status === 'STREAMED' ? tile.properties.rfs : undefined;

      const quote = {
        traderId: reply.TraderId,

        quoteId: reply.QuoteId,
        quoteType: isWithdraw ? 'Withdraw' : 'Tradeable',

        rfsId: reply.RfsId,
        rfsWindow: isDefined(previousStreamedRfs) ? previousStreamedRfs.quote.rfsWindow : reply.RfsWindow,
        rfsRemainingTime: reply.RfsWindow,

        defaultMargin: convertBidAskValue(reply.DefaultMargin),
        defaultSpotMargin: convertBidAskValue(reply.DefaultSpotMargin),
        spot: convertBidAskValue(reply.Spot),
        nearPoints: convertBidAskValue(reply.NearPoints),
        nearSalesPoints: convertBidAskValue(reply.NearSalesPoints),
        farPoints: convertBidAskValue(reply.FarPoints),
        farMktPoints: convertBidAskValue(reply.FarMktPoints),
        nearSalesAllInRate: convertBidAskValue(reply.NearSalesAllInRate),
        farSalesAllInRate: convertBidAskValue(reply.FarSalesAllInRate),
        nearDf2: reply.NearDf2,
        farDf2: reply.FarDf2,
        midAllInRate: reply.MidAllInRate,
        delta: reply.Delta,
        distributionStream: reply.DistributionStream,

        predealChecks: reply.PredealChecks.map((predealCheck) => {
          return predealCheck.Type === 'Icon'
            ? ({
                label: predealCheck.Label,
                statusHealth: predealCheck.StatusHealth,
                type: predealCheck.Type,
              } satisfies PredealCheckStatusIcon)
            : ({
                label: predealCheck.Label,
                statusHealth: predealCheck.StatusHealth,
                type: predealCheck.Type,
                statusValue: predealCheck.StatusValue,
              } satisfies PredealCheckStatusLabel);
        }),

        backendTimestamp: action.payload.backendTimestamp,
        frontendTimestamp: action.payload.frontendTimestamp,
      } satisfies SwapQuote;

      const rfs: SwapRfsPrice = {
        status: 'STREAMED',
        rfsId: requestId,
        requestedAt: tile.properties.rfs.requestedAt,
        quote,
        negociations: previousStreamedRfs?.negociations ?? [],
      };

      tile.properties.rfs = rfs;
      tile.properties.error = undefined;
    },

    swapRequestCanceled: (state: SwapById, action: PayloadAction<{ tileId: string }>) => {
      const tile = state[action.payload.tileId];

      // tile exists
      if (isNotDefined(tile)) {
        return;
      }

      tile.properties.rfs = undefined;
      tile.properties.nearPrice = undefined;
      tile.properties.commercialMarginSpotPoints = undefined;
      tile.properties.commercialMarginSwapPoints = undefined;

      if (!tile.isNonDeliverable) {
        tile.properties.farPrice = undefined;
      }
    },

    // only used to log timeout occurrences
    swapHeartbeatTimeout: (_state, _action: PayloadAction<{ tileId: string }>) => {},

    swapErrorOccurred: (
      state: SwapById,
      action: PayloadAction<{ tileId: string; errorType: 'tile' | 'rate'; errorCode: string; errorMessage: string }>,
    ) => {
      const tile = state[action.payload.tileId];

      // tile exists
      if (isNotDefined(tile)) {
        return;
      }

      tile.properties.error = {
        type: action.payload.errorType,
        code: action.payload.errorCode,
        message: action.payload.errorMessage,
      };
    },

    swapExecutionPending: (state, action: PayloadAction<{ tileId: string; executionId: string }>) => {
      const tile = state[action.payload.tileId];

      // tile exists
      if (isNotDefined(tile)) {
        return;
      }

      tile.execution = {
        status: 'PENDING',
        executionId: action.payload.executionId,
      };

      tile.properties.rfs = undefined;
    },

    swapExecutionSuccess: (state, action: PayloadAction<{ tileId: string }>) => {
      const tile = state[action.payload.tileId];

      // tile exists
      if (isNotDefined(tile) || isNotDefined(tile.execution)) {
        return;
      }

      tile.execution = {
        status: 'SUCCESS',
        executionId: tile.execution.executionId,
      };
    },

    swapExecutionRejected: (state, action: PayloadAction<{ tileId: string }>) => {
      const tile = state[action.payload.tileId];

      // tile exists
      if (isNotDefined(tile) || isNotDefined(tile.execution)) {
        return;
      }

      tile.execution = {
        status: 'REJECTED',
        executionId: tile.execution.executionId,
      };
    },

    swapRejectedExecutionReseted: (state, action: PayloadAction<{ tileId: string }>) => {
      const tile = state[action.payload.tileId];

      // tile exists
      if (isNotDefined(tile) || isNotDefined(tile.execution)) {
        return;
      }

      tile.execution = undefined;
    },

    swapExecutionReseted: (state, action: PayloadAction<{ tileId: string; userPreferences: UserPreferencesState }>) => {
      const tile = state[action.payload.tileId];

      // tile exists
      if (isNotDefined(tile) || isNotDefined(tile.execution)) {
        return;
      }

      const newTile = generateSwapTile(tile.properties.currencyPair, action.payload.userPreferences);

      state[action.payload.tileId] = {
        ...newTile,
        clientId: tile.clientId,
        properties: {
          ...newTile.properties,
          // keep some properties
          nearNotional: tile.properties.nearNotional,
          farNotional: tile.properties.farNotional,
          farExpiryDate: tile.properties.farExpiryDate,
          farExpiryDateTenor: tile.properties.farExpiryDateTenor,
          nearExpiryDateTenor: tile.properties.nearExpiryDateTenor,
          nearExpiryDate: tile.properties.nearExpiryDate,

          nearNotionalCurrencyIndex: tile.properties.nearNotionalCurrencyIndex,
          farNotionalCurrencyIndex: tile.properties.farNotionalCurrencyIndex,
        },
      };
    },
  },

  extraReducers: (builder) => {
    reduceSwapStateOnSharedTabRemoved(builder);

    //  ██████╗ ██████╗ ██╗██████╗     ████████╗██╗██╗     ███████╗
    // ██╔════╝ ██╔══██╗██║██╔══██╗    ╚══██╔══╝██║██║     ██╔════╝
    // ██║  ███╗██████╔╝██║██║  ██║       ██║   ██║██║     █████╗
    // ██║   ██║██╔══██╗██║██║  ██║       ██║   ██║██║     ██╔══╝
    // ╚██████╔╝██║  ██║██║██████╔╝       ██║   ██║███████╗███████╗
    //  ╚═════╝ ╚═╝  ╚═╝╚═╝╚═════╝        ╚═╝   ╚═╝╚══════╝╚══════╝

    builder.addCase(
      addTile,
      (
        state,
        {
          payload: {
            tile: { tileId, productType },
            userPreferences,
          },
        },
      ) => {
        // already defined
        if (isDefined(state[tileId])) {
          return state;
        }

        // not for a cash
        if (productType !== 'Swap') {
          return state;
        }

        // ProductType === Swap alors on crée une nouvelle tile
        return {
          ...state,
          [tileId]: generateSwapTile(undefined, userPreferences),
        };
      },
    );

    builder.addCase(duplicateTile, (state, { payload: { sourceTileId, newTileId, sessionId } }) => {
      // source undefined
      const sourceTile = state[sourceTileId];

      if (isNotDefined(sourceTile) || isNotDefined(sessionId)) {
        return state;
      }

      // already defined
      if (isDefined(state[newTileId])) {
        return state;
      }

      return {
        ...state,
        [newTileId]: {
          ...sourceTile,

          properties: {
            ...sourceTile.properties,

            // reset specific properties of the source
            rfs: undefined,

            commercialMarginSpotPoints: undefined,
            commercialMarginSwapPoints: undefined,
            commercialMarginAmountCurrencyIndex: undefined,

            error: undefined,
          },

          tradeCapture: {
            status: 'INITIALIZED',
            sessionId,
            version: -1,
          },
        },
      };
    });

    cashRepriceQuoteSwapReducer(builder);

    builder.addCase(updateTileProductType, (state, { payload: { tileId, productType, userPreferences } }) => {
      // Si tileId existe dans state, est que productType !== Swap alors on supprime la tile
      if (isDefined(state[tileId]) && productType !== 'Swap') {
        delete state[tileId];
        return;
      }

      // Si tileId n'existe pas et que productType === Swap, on créé une nouvelle tile
      if (isNotDefined(state[tileId]) && productType === 'Swap') {
        state[tileId] = generateSwapTile(undefined, userPreferences);
      }
    });

    builder.addCase(removeTile, (state, { payload: tileId }) => {
      if (isNotDefined(state[tileId])) {
        return state;
      }

      delete state[tileId];
    });

    // ████████╗██████╗  █████╗ ██████╗ ███████╗     ██████╗ █████╗ ██████╗ ████████╗██╗   ██╗██████╗ ███████╗
    // ╚══██╔══╝██╔══██╗██╔══██╗██╔══██╗██╔════╝    ██╔════╝██╔══██╗██╔══██╗╚══██╔══╝██║   ██║██╔══██╗██╔════╝
    //    ██║   ██████╔╝███████║██║  ██║█████╗      ██║     ███████║██████╔╝   ██║   ██║   ██║██████╔╝█████╗
    //    ██║   ██╔══██╗██╔══██║██║  ██║██╔══╝      ██║     ██╔══██║██╔═══╝    ██║   ██║   ██║██╔══██╗██╔══╝
    //    ██║   ██║  ██║██║  ██║██████╔╝███████╗    ╚██████╗██║  ██║██║        ██║   ╚██████╔╝██║  ██║███████╗
    //    ╚═╝   ╚═╝  ╚═╝╚═╝  ╚═╝╚═════╝ ╚══════╝     ╚═════╝╚═╝  ╚═╝╚═╝        ╚═╝    ╚═════╝ ╚═╝  ╚═╝╚══════╝

    builder.addMatcher(isAnyOf(swapApi.endpoints.checkSwapByTradeCapture.matchPending), (state, action) => {
      const tileId = action.meta.arg.originalArgs.tileId;

      const currentSwap = state[tileId];

      // cash must be not already executed
      if (isNotDefined(currentSwap) || isNotDefined(currentSwap.tradeCapture) || isDefined(currentSwap.execution)) {
        return;
      }

      const currentTradeCapture = currentSwap.tradeCapture;

      // optimistic updates — computed before pendingTradeCapture to derive isDateUnresolved
      const {
        nearExpiryDate: updatedNearExpiryDate,
        farExpiryDate: updatedFarExpiryDate,
        nearNotionalCurrencyIndex: updatedNearNotionalCurrencyIndex,
        farNotionalCurrencyIndex: updatedFaNotionalCurrencyIndex,
      } = action.meta.arg.originalArgs.properties;

      // check if the maturity date is the same date, or the same tenor
      // => don't update it, because if it is the case, the prod will not return an updated value
      //    and so, the prod will not restore the good value
      const nearExpiryDate =
        isDefined(updatedNearExpiryDate) &&
        updatedNearExpiryDate !== currentSwap.properties.nearExpiryDate &&
        updatedNearExpiryDate !== currentSwap.properties.nearExpiryDateTenor
          ? updatedNearExpiryDate
          : currentSwap.properties.nearExpiryDate;

      const farExpiryDate =
        isDefined(updatedFarExpiryDate) &&
        updatedFarExpiryDate !== currentSwap.properties.farExpiryDate &&
        updatedFarExpiryDate !== currentSwap.properties.farExpiryDateTenor
          ? updatedFarExpiryDate
          : currentSwap.properties.farExpiryDate;

      const isDateUnresolved =
        (isDefined(nearExpiryDate) && !isIsoDate(nearExpiryDate)) ||
        (isDefined(farExpiryDate) && !isIsoDate(farExpiryDate));

      const pendingTradeCapture: TradeCaptureChecked<FxSwapRegular['properties']> =
        currentTradeCapture.status === 'INITIALIZED' ||
        currentTradeCapture.status === 'CHECKING' ||
        currentTradeCapture.status === 'ERROR'
          ? {
              status: 'CHECKING',
              sessionId: currentTradeCapture.sessionId,
              version: currentTradeCapture.version,
              isReadyToPrice: false,
              isPriceObsolete: false,
              isProductObsolete: false,

              warnings: {},
              errors: {},
            }
          : {
              status: 'CHECKING',
              sessionId: currentTradeCapture.sessionId,
              version: currentTradeCapture.version,
              isReadyToPrice: isDateUnresolved ? false : currentTradeCapture.isReadyToPrice,
              isPriceObsolete: currentTradeCapture.isPriceObsolete,
              isProductObsolete: currentTradeCapture.isProductObsolete,

              warnings: currentTradeCapture.warnings,
              errors: currentTradeCapture.errors,
            };

      // nearNotionalCurrencyIndex and farNotionalCurrencyIndex have to be synchronized
      // trade capture synchronize them only when near / far notional is defined
      // so force here the synthonization after any update of one of them
      const nearNotionalCurrencyIndex = isDefined(updatedNearNotionalCurrencyIndex)
        ? updatedNearNotionalCurrencyIndex
        : isDefined(updatedFaNotionalCurrencyIndex)
          ? updatedFaNotionalCurrencyIndex
          : currentSwap.properties.nearNotionalCurrencyIndex;

      const farNotionalCurrencyIndex = isDefined(updatedFaNotionalCurrencyIndex)
        ? updatedFaNotionalCurrencyIndex
        : isDefined(updatedNearNotionalCurrencyIndex)
          ? updatedNearNotionalCurrencyIndex
          : currentSwap.properties.farNotionalCurrencyIndex;

      // rfs and error are intentionally NOT updated from the action args:
      // they are pricing-specific fields that must never be overwritten by an optimistic update.
      //
      // ObjectWithUndefinedPropertiesNullable turns `T | undefined` into `T | null | undefined`.
      // The store uses `T | undefined`, so null values are coerced back to undefined with `?? undefined`.
      const args = action.meta.arg.originalArgs;

      // Common fields — present in both FxSwapRegular and FxSwapNds properties.
      // Order follows the property declaration order in state.ts.
      // TypeScript accepts these assignments without narrowing the union.
      currentSwap.properties.currencyPair = args.properties.currencyPair ?? currentSwap.properties.currencyPair;
      currentSwap.properties.currency1 = args.properties.currency1 ?? currentSwap.properties.currency1;
      currentSwap.properties.currency2 = args.properties.currency2 ?? currentSwap.properties.currency2;
      currentSwap.properties.nearNotional = args.properties.nearNotional ?? currentSwap.properties.nearNotional;
      currentSwap.properties.nearNotionalCurrencyIndex = nearNotionalCurrencyIndex;
      currentSwap.properties.nearExpiryDate = nearExpiryDate;
      currentSwap.properties.nearExpiryDateTenor =
        args.properties.nearExpiryDateTenor ?? currentSwap.properties.nearExpiryDateTenor;
      currentSwap.properties.nearPrice = args.properties.nearPrice ?? currentSwap.properties.nearPrice;
      currentSwap.properties.farNotional = args.properties.farNotional ?? currentSwap.properties.farNotional;
      currentSwap.properties.farNotionalCurrencyIndex = farNotionalCurrencyIndex;
      currentSwap.properties.farExpiryDate = farExpiryDate;
      currentSwap.properties.farExpiryDateTenor =
        args.properties.farExpiryDateTenor ?? currentSwap.properties.farExpiryDateTenor;
      currentSwap.properties.commercialMarginSpotPoints =
        args.properties.commercialMarginSpotPoints ?? currentSwap.properties.commercialMarginSpotPoints;
      currentSwap.properties.commercialMarginSwapPoints =
        args.properties.commercialMarginSwapPoints ?? currentSwap.properties.commercialMarginSwapPoints;
      currentSwap.properties.commercialMarginAmountCurrencyIndex =
        args.properties.commercialMarginAmountCurrencyIndex ??
        currentSwap.properties.commercialMarginAmountCurrencyIndex;

      // Type-specific fields — narrow the union to access fields exclusive to each variant.
      // Order follows the property declaration order in state.ts.
      if (!currentSwap.isNonDeliverable && !args.isNonDeliverable) {
        // FxSwapRegular: type (after isPriceable) and farPrice (after farExpiryDateTenor)
        currentSwap.properties.type = args.properties.type ?? currentSwap.properties.type;
        currentSwap.properties.farPrice = args.properties.farPrice ?? currentSwap.properties.farPrice;
      } else if (currentSwap.isNonDeliverable && args.isNonDeliverable) {
        // FxSwapNds: NDS-specific fields after commercialMarginAmountCurrencyIndex
        currentSwap.properties.isCrossed = args.properties.isCrossed ?? currentSwap.properties.isCrossed;
        currentSwap.properties.fixingCurrency = args.properties.fixingCurrency ?? currentSwap.properties.fixingCurrency;
        currentSwap.properties.xCurrency = args.properties.xCurrency ?? currentSwap.properties.xCurrency;
        currentSwap.properties.possibleXCurrencies =
          args.properties.possibleXCurrencies ?? currentSwap.properties.possibleXCurrencies;
        currentSwap.properties.fixingSource = args.properties.fixingSource ?? currentSwap.properties.fixingSource;
        currentSwap.properties.possibleFixingSources =
          args.properties.possibleFixingSources ?? currentSwap.properties.possibleFixingSources;
        currentSwap.properties.sndFixingSource =
          args.properties.sndFixingSource ?? currentSwap.properties.sndFixingSource;
        currentSwap.properties.possibleSndFixingSources =
          args.properties.possibleSndFixingSources ?? currentSwap.properties.possibleSndFixingSources;
        currentSwap.properties.nearFixingDate = args.properties.nearFixingDate ?? currentSwap.properties.nearFixingDate;
        currentSwap.properties.farFixingDate = args.properties.farFixingDate ?? currentSwap.properties.farFixingDate;
      }

      currentSwap.tradeCapture = pendingTradeCapture;
    });

    builder.addMatcher(isAnyOf(swapApi.endpoints.checkSwapByTradeCapture.matchFulfilled), (state, action) => {
      const tileId = action.meta.arg.originalArgs.tileId;

      const currentSwap = state[tileId];
      const swapFromTradeCapture = action.payload;

      if (isNotDefined(currentSwap)) {
        console.error(`swap tile ${tileId} not found in store while processing trade capture`);
        return;
      }

      if (isNotDefined(swapFromTradeCapture) || isDefined(currentSwap.execution)) {
        // cash must be not already executed
        console.error(
          `swap tile ${tileId} received invalid trade capture or has already been executed, cannot update it`,
        );
        return;
      }

      if (isNotDefined(currentSwap.tradeCapture)) {
        console.error(`swap tile ${tileId} has no trade capture data`);
        return;
      }

      state[tileId] = updateSwapFromTradeCapture(currentSwap, currentSwap.tradeCapture.sessionId, swapFromTradeCapture);
    });

    // ██████╗ ███████╗███████╗
    // ██╔══██╗██╔════╝██╔════╝
    // ██████╔╝█████╗  ███████╗
    // ██╔══██╗██╔══╝  ╚════██║
    // ██║  ██║██║     ███████║
    // ╚═╝  ╚═╝╚═╝     ╚══════╝

    builder.addMatcher(swapApi.endpoints.requestSwapRfs.matchPending, (state, action) => {
      const tileId = action.meta.arg.originalArgs.tileId;

      const tile = state[tileId];

      if (isNotDefined(tile)) {
        console.error(`swap tile ${tileId} not found in store while processing RFS request`);
        return;
      }

      tile.properties.rfs = {
        status: 'PENDING',
        rfsId: action.meta.arg.originalArgs.rfsId,
        requestedAt: action.meta.arg.originalArgs.requestedAt,
      };
    });

    builder.addMatcher(swapApi.endpoints.requestSwapRfs.matchRejected, (state, action) => {
      const tileId = action.meta.arg.originalArgs.tileId;

      const tile = state[tileId];

      if (isNotDefined(tile)) {
        console.error(`swap tile ${tileId} not found in store while processing RFS request`);
        return;
      }

      tile.properties.error = {
        type: 'rate',
        code: 'RFS_REQUEST_FAILED',
        message: 'RFS request failed',
      };
    });

    // ██████╗ ███████╗ ██████╗ ██████╗ ██████╗ ██████╗     ██████╗ ██████╗ ██╗ ██████╗███████╗███████╗
    // ██╔══██╗██╔════╝██╔════╝██╔═══██╗██╔══██╗██╔══██╗    ██╔══██╗██╔══██╗██║██╔════╝██╔════╝██╔════╝
    // ██████╔╝█████╗  ██║     ██║   ██║██████╔╝██║  ██║    ██████╔╝██████╔╝██║██║     █████╗  ███████╗
    // ██╔══██╗██╔══╝  ██║     ██║   ██║██╔══██╗██║  ██║    ██╔═══╝ ██╔══██╗██║██║     ██╔══╝  ╚════██║
    // ██║  ██║███████╗╚██████╗╚██████╔╝██║  ██║██████╔╝    ██║     ██║  ██║██║╚██████╗███████╗███████║
    // ╚═╝  ╚═╝╚══════╝ ╚═════╝ ╚═════╝ ╚═╝  ╚═╝╚═════╝     ╚═╝     ╚═╝  ╚═╝╚═╝ ╚═════╝╚══════╝╚══════╝

    builder.addMatcher(mifidApi.endpoints.recordPrice.matchFulfilled, (state, action) => {
      const tile = state[action.meta.arg.originalArgs.tileId];

      if (isNotDefined(tile) || tile.properties.rfs?.status !== 'STREAMED') {
        return;
      }

      tile.properties.rfs.negociations.push({
        date: formatISO9075(action.meta.arg.originalArgs.date),
      });
    });

    // ██╗    ██╗ ██████╗ ██████╗ ██╗  ██╗███████╗██████╗  █████╗  ██████╗███████╗
    // ██║    ██║██╔═══██╗██╔══██╗██║ ██╔╝██╔════╝██╔══██╗██╔══██╗██╔════╝██╔════╝
    // ██║ █╗ ██║██║   ██║██████╔╝█████╔╝ ███████╗██████╔╝███████║██║     █████╗
    // ██║███╗██║██║   ██║██╔══██╗██╔═██╗ ╚════██║██╔═══╝ ██╔══██║██║     ██╔══╝
    // ╚███╔███╔╝╚██████╔╝██║  ██║██║  ██╗███████║██║     ██║  ██║╚██████╗███████╗
    //  ╚══╝╚══╝  ╚═════╝ ╚═╝  ╚═╝╚═╝  ╚═╝╚══════╝╚═╝     ╚═╝  ╚═╝ ╚═════╝╚══════╝

    builder.addMatcher(getWorkspaceMatchFulfilled, (state, action) => {
      if (!action.payload) {
        return;
      }
      Object.keys(state).forEach((id) => {
        delete state[id];
      }); // clear all products created before workspace has loaded
      action.payload.products.swap.forEach((swapProduct) => {
        state[swapProduct.tileId] = mapToFxSwap(swapProduct);
      });
    });

    reduceSwapOnFetchSharedWorkspace(builder);
  },

  selectors: {
    getAllSwapProducts: (state) => Object.entries(state).map(([tileId, product]) => ({ tileId, ...product })),
  },
});

export const {
  swapReseted,

  swapCommercialMarginSpotPointsUpdated,
  swapCommercialMarginSwapPointsUpdated,
  swapCommercialMarginCurrencyToggled,

  swapRfsQuoteReceived,
  swapRequestCanceled,
  swapHeartbeatTimeout,

  swapExecutionPending,
  swapExecutionSuccess,
  swapExecutionRejected,
  swapRejectedExecutionReseted,
  swapExecutionReseted,
  swapClientIdUpdated,

  swapErrorOccurred,
} = swapSlice.actions;
export const { getAllSwapProducts } = swapSlice.selectors;
